import React, { createContext, useContext, useState, useEffect } from 'react';
import api, { loginUser, logoutUser } from '../services/api';
import { useNavigate } from 'react-router-dom';

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return null;
};

// Create the AuthContext
const AuthContext = createContext();

// AuthProvider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // Stores user data
  const [isAuthenticated, setIsAuthenticated] = useState(false); // Tracks auth status
  const [emailVerified, setEmailVerified] = useState(false); // Tracks email verification
  const [loading, setLoading] = useState(true); // Tracks loading state
  
  // Check authentication status when the component mounts
  useEffect(() => {
    const checkAuthStatus = async () => {
      setLoading(true);
      const token = localStorage.getItem('token') || getCookie('token');
      if (token) {
        // Attach token to Axios headers
        api.defaults.headers.Authorization = `Token ${token}`;
        try {
          const response = await api.get('/users/check/');
          // console.log('Auth check response:', response.data); // Debug response

          // Update user and auth status based on the response
          if (response.data.isAuthenticated) {
            setUser({
              ...response.data.user,
              id: response.data.user.id, // Ensure ID is included
              tutor_profile: response.data.user.tutor_profile
                ? { id: response.data.user.tutor_profile.id }
                : null,
            });
            setEmailVerified(
              !!response.data.user.userprofile?.email_verified
            );
            setIsAuthenticated(true);
          } else {
            setUser(null);
            setIsAuthenticated(false);
            setEmailVerified(false);
          }
        } catch (err) {
          console.error('Auth check error:', err.response?.data || err.message); // Debug error
          await logoutUser();
          setUser(null);
          setIsAuthenticated(false);
          setEmailVerified(false);
        }
      } else {
        setUser(null);
        setIsAuthenticated(false);
        setEmailVerified(false);
      }
      setLoading(false); // End loading state
    };

    checkAuthStatus();
  }, []); // Runs only once on mount

  // Login function
  const login = async (credentials) => {
    try {
      const data = await loginUser(credentials); // Call the API login method
      console.log('Login response data:', data); // Debug the login response
      localStorage.setItem('token', data.token); // Save token in localStorage
      api.defaults.headers.Authorization = `Token ${data.token}`;

      // Fetch full user data including profile
      const resp = await api.get('/users/check/');
      let verified = data.email_verified;
      if (resp.data.isAuthenticated) {
        setUser({
          ...resp.data.user,
          tutor_profile: resp.data.user.tutor_profile
            ? { id: resp.data.user.tutor_profile.id }
            : null,
        });
        verified = !!resp.data.user.userprofile?.email_verified;
      } else {
        setUser({
          ...data.user,
          tutor_profile: data.user.tutor_profile
            ? { id: data.user.tutor_profile.id }
            : null,
        }); // Fallback
      }
      setEmailVerified(!!verified);

      setIsAuthenticated(true); // Mark as authenticated
      return { emailVerified: !!verified };
    } catch (err) {
      console.error('Login error:', err.response?.data || err.message); // Debug error
      setUser(null);
      setIsAuthenticated(false);
      setEmailVerified(false);
      throw err; // Propagate the error to the caller
    }
  };

  // Logout function
  const logout = async () => {
    await logoutUser(); // Clear token and headers and inform backend
    setUser(null); // Reset state
    setIsAuthenticated(false);
  };

  // Provide context values to children components
  return (
    <AuthContext.Provider value={{ user, isAuthenticated, emailVerified, login, logout, loading, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom Hook to use AuthContext
export const useAuth = () => useContext(AuthContext);
